import { CampaignMedia, MediaFile } from '@/types/media';
import { FirebaseAppFirestore } from '@/firebase/firebase-app';
import { getCurrentUser } from '@/firebase/firebase-user';
import { extendWithUpdatedAt, updateDocumentRef } from '@/utils/db-hooks';
import firebaseNames from '@/statics/firebase-names';
import { SYSTEM_STATUS } from '@/statics/system-status';
import {
  Campaign,
  CampaignRef,
  CampaignRequest,
  CampaignTuple,
  ShashaAd,
  StoreCampaign,
  StoreOwnerCampaign,
  StoreOwnerIncomingCampaign
} from '@/types/campaigns';
import { ScreenCluster } from '@/types/screen-cluster';
import firebase from 'firebase';
import firestore = firebase.firestore;
import { AdSpace } from '@/types/adspaces';
import { SystemStatus } from '@/types/company-profile';
import { chunk, flow, flatten, map, head, last } from 'lodash';
import { FILTER_QUERY_LIMIT } from '@/statics/main';
import DocumentSnapshot = firebase.firestore.DocumentSnapshot;
import QuerySnapshot = firebase.firestore.QuerySnapshot;
import { getUserById } from '@/actions/users/actions';

/**
 * Approve campaign as store owner.
 * @param campaignRequest Campaign
 */
export const approveCampaignAction = async (
  campaignRequest: CampaignRequest
): Promise<void> => {
  try {
    const currentUser = await getCurrentUser();
    const { uid } = currentUser!;
    const { ADVERTISER_UID: userId, ID: campaignId } = campaignRequest;

    const advertiser = await getUserById(userId);
    const batch = FirebaseAppFirestore.batch();
    if (!advertiser?.HAS_PUBLISHED_CAMPAIGNS) {
      const advertiserRef = FirebaseAppFirestore.collection(
        firebaseNames.USERS_INFO
      )
        .doc(userId);
      batch.update(advertiserRef, extendWithUpdatedAt({ HAS_PUBLISHED_CAMPAIGNS: true }));
    }
    const campaignRef = FirebaseAppFirestore.collection(
      firebaseNames.CAMPAIGNS.VAL
    )
      .doc(userId)
      .collection(firebaseNames.CAMPAIGNS.USER_CAMPAIGNS)
      .doc(campaignId);
    batch.update(campaignRef, extendWithUpdatedAt({
      ACTION_STORE_OWNER_ID: uid,
      APPROVED_BY_STORE_OWNER: true,
      STATUS: SYSTEM_STATUS.UPCOMING,
    }));
    return batch.commit();
  } catch (error) {
    console.error('error when approve store owner: ', error)
    throw error;
  }
};

/**
 * Approve campaign media change request as store owner.
 * @param campaignRequest
 * @param mediaList
 */
export const approveCampaignMediaChangeAction = async (
  campaignRequest: CampaignRequest,
  mediaList: MediaFile[]
): Promise<void> => {
  const {
    ADVERTISER_UID: userId,
    ID: campaignId,
  } = campaignRequest;

  const currentUser = await getCurrentUser();
  const { uid } = currentUser!;

  const campaignRef = FirebaseAppFirestore.collection(
    firebaseNames.CAMPAIGNS.VAL
  )
    .doc(userId)
    .collection(firebaseNames.CAMPAIGNS.USER_CAMPAIGNS)
    .doc(campaignId);

  const campaignDbDataObject = (await campaignRef.get()).data()
  if (!campaignDbDataObject) {
    throw new Error('Campaign not found!')
  }
  const updatedMediaList: CampaignMedia[] = campaignDbDataObject.MEDIA_LIST.map((media: CampaignMedia) => {
    return media.MEDIA_CHANGE_REQUEST ? media.MEDIA_CHANGE_REQUEST : media;
  });

  await updateDocumentRef(
    campaignRef,
    {
      MEDIA_LIST: updatedMediaList,
      MEDIA_CHANGE_ACTION_STORE_OWNER_ID: uid,
      APPROVED_BY_STORE_OWNER: true,
      MEDIA_CHANGE_REQUEST_STATUS: SYSTEM_STATUS.APPROVED,
    }
  );
};

/**
 * Reject campaign media change request as store owner.
 * @param campaignRequest
 * @param rejectionReason
 */
export const rejectCampaignMediaChangeAction = async (
  campaignRequest: CampaignRequest,
  rejectionReason: string
): Promise<void> => {
  const {
    ADVERTISER_UID: userId,
    ID: campaignId,
  } = campaignRequest;

  const currentUser = await getCurrentUser();
  const { uid } = currentUser!;

  const campaignRef = FirebaseAppFirestore.collection(
    firebaseNames.CAMPAIGNS.VAL
  )
    .doc(userId)
    .collection(firebaseNames.CAMPAIGNS.USER_CAMPAIGNS)
    .doc(campaignId);

  await updateDocumentRef(
    campaignRef,
    {
      REJECTED_AT: firebase.firestore.Timestamp.now(),
      MEDIA_CHANGE_REQUEST_STATUS: SYSTEM_STATUS.REJECTED,
      COMMENTS: rejectionReason,
      MEDIA_CHANGE_ACTION_STORE_OWNER_ID: uid,
    }
  );
};

/**
 * Reject campaign as store owner.
 * @param campaignRequest
 * @param rejectionReason
 */
export const rejectCampaignAction = async (
  campaignRequest: CampaignRequest,
  rejectionReason: string
): Promise<void> => {
  const currentUser = await getCurrentUser();
  const { uid } = currentUser!;
  const { ADVERTISER_UID: userId, ID: campaignId } = campaignRequest;
  const campaignRef = FirebaseAppFirestore.collection(
    firebaseNames.CAMPAIGNS.VAL
  )
    .doc(userId)
    .collection(firebaseNames.CAMPAIGNS.USER_CAMPAIGNS)
    .doc(campaignId);

  await updateDocumentRef(
    campaignRef,
    {
      ACTION_STORE_OWNER_ID: uid,
      APPROVED_BY_STORE_OWNER: false,
      REJECTED_AT: firebase.firestore.Timestamp.now(),
      STATUS: SYSTEM_STATUS.REQUIRES_ACTION,
      COMMENTS: rejectionReason,
    }
  );
};

/**
 * Load store owner created campaigns
 */
export const loadStoreOwnerCreatedCampaignsTuples = async (): Promise<CampaignTuple[]> => {
  const currentUser = await getCurrentUser();
  const campaignsSnap = await FirebaseAppFirestore.collection(
    firebaseNames.CAMPAIGNS.VAL
  )
    .doc(currentUser!.uid)
    .collection(firebaseNames.CAMPAIGNS.USER_CAMPAIGNS)
    .get();

  if (campaignsSnap.empty) {
    return [];
  }

  return campaignsSnap.docs.map((doc) => {
    return [doc.data(), doc.ref] as CampaignTuple;
  });
};

/**
 * Load all campaigns assigned to current user's store.
 */
export const loadStoreCampaignsReferencesAction = async (): Promise<firestore.DocumentReference[]> => {
  const currentUser = await getCurrentUser();
  const campaignsSnap = await FirebaseAppFirestore.collection(
    firebaseNames.CAMPAIGNS.VAL
  )
    .doc(currentUser!.uid)
    .collection(firebaseNames.CAMPAIGNS.STORE_CAMPAIGNS)
    .get();

  if (campaignsSnap.empty) {
    return [];
  }

  const dataPromiseArray = campaignsSnap.docs.map(async (doc) => {
    const campaignRef = doc.data().campaignRef;
    return campaignRef;
  });
  return Promise.all(dataPromiseArray);
};

/**
 * Load last five campaign refs assigned to current user's store.
 */
export const loadLatestStoreCampaignsReferencesAction = async (): Promise<firestore.DocumentReference[]> => {
  const currentUser = await getCurrentUser();
  const campaignsSnap = await FirebaseAppFirestore.collection(
    firebaseNames.CAMPAIGNS.VAL
  )
    .doc(currentUser!.uid)
    .collection(firebaseNames.CAMPAIGNS.STORE_CAMPAIGNS)
    .limit(5)
    .get();

  if (campaignsSnap.empty) {
    return [];
  }

  const dataPromiseArray = campaignsSnap.docs.map(async (doc) => {
    const campaignRef = doc.data().campaignRef;
    return campaignRef;
  });

  return Promise.all(dataPromiseArray);
};


/**
 * Load store owner campaigns
 */
// export const loadStoreOwnerCampaignsAction = async (): Promise<CampaignTuple[]> => {
//   try {
//     const storeCampaigns = await loadStoreCampaignsTuples();
//     const storeCreatedCampaigns = await loadStoreOwnerCreatedCampaignsTuples();
//     return [...storeCampaigns, ...storeCreatedCampaigns];
//   } catch (err) {
//     /* tslint:disable-next-line */
//     console.error(err.stack);
//     throw err;
//   }
// };

/**
 * Load store campaigns
 */
export const loadStoreCampaigns = async (): Promise<Campaign[]> => {
  const campaignsRefs = await loadStoreCampaignsReferencesAction();
  return getCampaignsByRefs(campaignsRefs);
};
export const loadStoreOwnerCampaigns = async (storeOwnerScreenClustersIds: ScreenCluster['ID'][], status: SystemStatus['VAL']): Promise<StoreOwnerIncomingCampaign[]> => {
  return flow([
    (screenClusterIds: ScreenCluster['ID'][]) => chunk(screenClusterIds, FILTER_QUERY_LIMIT),
    (chunks: ScreenCluster['ID'][][]) => map(chunks, (filterChunk) =>
      FirebaseAppFirestore.collection(firebaseNames.STORE_OWNER_CAMPAIGNS.VAL)
        .where('SCREEN_CLUSTERS_IDS', 'array-contains-any', filterChunk)
        .where('STATUS.VAL', '==', status)
        .get()
    ),
    async (snapshots: Promise<QuerySnapshot<StoreOwnerIncomingCampaign>>[]) => {
      const allData = await Promise.all(snapshots);
      return flatten(allData.map((snapshot: QuerySnapshot<StoreOwnerIncomingCampaign>) =>
        snapshot.docs.map((doc: DocumentSnapshot<StoreOwnerIncomingCampaign>) => {
          const campaign = doc.data() as StoreOwnerIncomingCampaign;
          campaign.isStoreOwner = true;
          return campaign;
        })
      ));
    }
  ])(storeOwnerScreenClustersIds);
};

export const loadCompletedStoreOwnerCampaigns = async (storeOwnerScreenClustersIds: ScreenCluster['ID'][]): Promise<StoreOwnerIncomingCampaign[]> => {
  type SnapshotTuple = [QuerySnapshot<StoreOwnerIncomingCampaign>, QuerySnapshot<StoreOwnerIncomingCampaign>];

  return flow([
    (screenClusterIds: ScreenCluster['ID'][]) => chunk(screenClusterIds, FILTER_QUERY_LIMIT),
    (chunks: ScreenCluster['ID'][][]) => map(chunks, (filterChunk) =>
      Promise.all([
        FirebaseAppFirestore.collection(firebaseNames.STORE_OWNER_CAMPAIGNS.VAL)
          .where('SCREEN_CLUSTERS_IDS', 'array-contains-any', filterChunk)
          .where('STATUS.VAL', '==', SYSTEM_STATUS.FINISHED.VAL)
          .get(),
        FirebaseAppFirestore.collection(firebaseNames.STORE_OWNER_CAMPAIGNS.VAL)
          .where('SCREEN_CLUSTERS_IDS', 'array-contains-any', filterChunk)
          .where('STATUS.VAL', '==', SYSTEM_STATUS.CANCELED.VAL)
          .get()
      ]) as Promise<SnapshotTuple>
    ),
    async (chunkPromises: Promise<SnapshotTuple>[]) => {
      const chunksResults = await Promise.all(chunkPromises);
      return flatten(chunksResults.map((snapshots: SnapshotTuple) => {
        const finishedSnap = head(snapshots);
        const canceledSnap = last(snapshots);
        if (!finishedSnap || !canceledSnap) {
          return [];
        }
        return [...finishedSnap.docs, ...canceledSnap.docs]
          .map((doc: DocumentSnapshot<StoreOwnerIncomingCampaign>) => {
            const campaign = doc.data() as StoreOwnerIncomingCampaign;
            campaign.isStoreOwner = true;
            return campaign;
          });
      }));
    }
  ])(storeOwnerScreenClustersIds);
};

export const loadAdminCampaigns = async (adSpaceIds: AdSpace['ID'][], status: SystemStatus['VAL']): Promise<StoreOwnerIncomingCampaign[]> => {
  return flow([
    (ids: AdSpace['ID'][]) => chunk(ids, FILTER_QUERY_LIMIT),
    (chunks: AdSpace['ID'][][]) => map(chunks, (filterChunk) =>
      FirebaseAppFirestore.collection(firebaseNames.SHASHA_ADS.VAL)
        .where('AD_SPACE_ID', 'in', filterChunk)
        .where('STATUS.VAL', '==', status)
        .get()
    ),
    async (snapshots: Promise<QuerySnapshot<ShashaAd>>[]) => {
      const allData = await Promise.all(snapshots);
      return flatten(allData.map((snapshot: QuerySnapshot<ShashaAd>) =>
        snapshot.docs.map((doc: DocumentSnapshot<ShashaAd>) => {
          const campaign = doc.data() as ShashaAd;
          (campaign as unknown as Campaign).MEDIA_LIST = [(campaign.MEDIA_FILE as CampaignMedia)];
          campaign.isShashaAd = true;
          return campaign as unknown as StoreOwnerIncomingCampaign;
        })
      ));
    }
  ])(adSpaceIds);
};
/**
 * @description
 * Get all Screen clusters in Ad spaces of store owner.
 */
export const getStoreScreenClusters = async (): Promise<ScreenCluster[]> => {
  const currentUser = await getCurrentUser();
  const userAdSpacesSnap = await FirebaseAppFirestore.collection(
    firebaseNames.AD_SPACES
  )
    .where('SPACE_OWNER_ID', '==', currentUser!.uid)
    .get();

  if (userAdSpacesSnap.empty) {
    return [];
  }

  const storeScreenClustersSnaps = userAdSpacesSnap.docs
    .map((adSpaceDocumentSnapshot) => adSpaceDocumentSnapshot.data() as AdSpace)
    .map((adSpace) => adSpace.SCREEN_CLUSTER_REF as firebase.firestore.DocumentReference<ScreenCluster>)
    .filter(Boolean)
    .map((documentRef) => documentRef.get());

  return (await Promise.all(storeScreenClustersSnaps)).map((snapshot) =>
    snapshot.data()
  ) as ScreenCluster[];
};

const getCampaignsByRefs = async (
  refs: firestore.DocumentReference[]
): Promise<Campaign[]> => {
  const campaignsPromises = refs.map(async (ref: CampaignRef) => {
    const snap = await ref.get();
    return snap.data() as Campaign;
  });

  const campaigns = await Promise.all(campaignsPromises);
  return campaigns.filter((item) => item);
};

/**
 * @description
 * Get last five store campaigns.
 */
export const loadLatestStoreCampaigns = async (): Promise<Campaign[]> => {
  const campaignsRefs = await loadLatestStoreCampaignsReferencesAction();
  return getCampaignsByRefs(campaignsRefs);
};

export const cancelCampaignAction = async (campaign: StoreCampaign) => {
  const campaignRef = FirebaseAppFirestore.collection(firebaseNames.STORE_OWNER_CAMPAIGNS.VAL).doc(campaign.id);

  await updateDocumentRef(
    campaignRef,
    { STATUS: SYSTEM_STATUS.CANCELED } as Partial<StoreOwnerCampaign>
  );
}

